import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  digitCapacity,
  formatDigitsAsDisplay,
  msToDigits,
  parseTimeFromDigits,
} from '../utils/timeFormat';
import type { TimeFormat } from '../types/domain';

interface TimeDisplayInputProps {
  valueMs: number | null;
  onChangeMs: (ms: number | null) => void;
  format: TimeFormat;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Volitelne — stisk "Dalsi" / Enter zavola tento callback. */
  onNext?: () => void;
  /** Pokud true, komponenta poslucha globalni klavesnici (lze vypnout pokud neni aktivni). */
  keyboardEnabled?: boolean;
  /** Skryje interni format toggle (SS,cc / M:SS,cc). Rodic pak muze toggle zobrazit jinde. */
  hideFormatToggle?: boolean;
  /** Kompaktnejsi layout (mensi font, mensi mezery) — vhodne pro omezeny prostor. */
  compact?: boolean;
}

/**
 * Velky displej casu se "stopky" UX: cislice se pripisuji zprava, posun predchozich o 1 pozici doleva.
 * - Desktop: pouziti fyzicke klavesnice (0-9, Backspace, Esc, Enter)
 * - Tablet/Mobil: inline numericky keypad 3x4
 */
export const TimeDisplayInput = ({
  valueMs,
  onChangeMs,
  format,
  label,
  disabled = false,
  autoFocus = false,
  onNext,
  keyboardEnabled = true,
  hideFormatToggle = false,
  compact = false,
}: TimeDisplayInputProps) => {
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const isFinePointer = useMediaQuery('(pointer: fine)');
  // Keypad zobrazujeme vzdy — funguje na mysi, dotyku i tabletu. Hint o klavesnici ukazujeme jen s fine pointerem (desktop).
  const showKeypad = true;
  const showKeyboardHint = isFinePointer && !isCoarsePointer;

  // Lokalni override formatu — pokud uzivatel potrebuje zadat cas v minutach u SS,cc discipliny.
  // Zobrazovaci/parsing format se pouziva tento, data se stale ukladaji v ms.
  const [displayFormat, setDisplayFormat] = useState<TimeFormat>(format);
  useEffect(() => {
    // Pri zmene prop formatu (nova disciplina) resetuj override.
    setDisplayFormat(format);
  }, [format]);

  const [digits, setDigits] = useState<string>(() => msToDigits(valueMs, displayFormat));

  // Zrcadlo aktualni hodnoty digits v useRef. Ridi se u rychle po sobe jdoucich stisku
  // (1,1,7,9,6), aby append/pop videly AKTUALNI buffer misto stale closure value —
  // setState je asynchronni a dva stisky v jednom tiku by jinak videly stejny prev.
  const digitsRef = useRef(digits);
  digitsRef.current = digits;

  // Sync z parentu: pokud valueMs/format zvenci neodpovida internimu bufferu
  // (napr. parent prepnul na jiny team/lane), preklopim interni state.
  // Porovnavame pres msToDigits, aby se diff nekruhove nesmyckoval.
  useEffect(() => {
    const expected = msToDigits(valueMs, displayFormat);
    if (expected !== digits) {
      setDigits(expected);
      digitsRef.current = expected;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMs, displayFormat]);

  const capacity = digitCapacity(displayFormat);

  const commit = useCallback(
    (next: string) => {
      digitsRef.current = next;
      setDigits(next);
      onChangeMs(parseTimeFromDigits(next, displayFormat));
    },
    [displayFormat, onChangeMs],
  );

  const append = useCallback(
    (d: string) => {
      if (disabled) return;
      if (!/^[0-9]$/.test(d)) return;
      commit((digitsRef.current + d).slice(-capacity));
      if (navigator.vibrate) navigator.vibrate(8);
    },
    [capacity, commit, disabled],
  );

  const pop = useCallback(() => {
    if (disabled) return;
    commit(digitsRef.current.slice(0, -1));
  }, [commit, disabled]);

  const clear = useCallback(() => {
    if (disabled) return;
    commit('');
  }, [commit, disabled]);

  // Fyzicka klavesnice (desktop) — armujeme az po otevreni dialogu,
  // aby nezachytila klavesy doruceeene behem transitionu (napr. Enter z predchoziho dialogu).
  useEffect(() => {
    if (!keyboardEnabled || disabled) return;
    let armed = false;
    const armTimer = window.setTimeout(() => { armed = true; }, 150);
    const onKey = (e: KeyboardEvent) => {
      if (!armed) return;
      // Ignoruj pokud se fokusuje na jiny input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        append(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        pop();
      } else if (e.key === 'Escape') {
        // Esc v inputu = vynulovat; zastavime propagaci, aby MUI Dialog
        // nezachytil event a nezavrel cely dialog.
        e.preventDefault();
        e.stopPropagation();
        clear();
      } else if (e.key === 'Enter' && onNext) {
        e.preventDefault();
        e.stopPropagation();
        onNext();
      }
    };
    // capture=true, aby jsme klavesu zachytili JESTE PRED MUI Dialog listenerem (ten je v bubble fazi).
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.clearTimeout(armTimer);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [append, pop, clear, onNext, keyboardEnabled, disabled]);

  const displayText = useMemo(() => formatDigitsAsDisplay(digits, displayFormat), [digits, displayFormat]);
  const isEmpty = digits.length === 0;

  const handleFormatToggle = useCallback(
    (_: unknown, next: TimeFormat | null) => {
      if (!next || next === displayFormat) return;
      setDisplayFormat(next);
      // Pri prepnutu zachovej aktualni ms a regeneruj digits pro novy format.
      const currentMs = parseTimeFromDigits(digits, displayFormat);
      setDigits(msToDigits(currentMs, next));
    },
    [digits, displayFormat],
  );

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {label}
        </Typography>
      )}

      {/* Prepnac formatu — umoznuje rucne zadat cas v minutach u SS,cc discipliny */}
      {!hideFormatToggle && (
        <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
          <ToggleButtonGroup
            size="small"
            value={displayFormat}
            exclusive
            onChange={handleFormatToggle}
            disabled={disabled}
            aria-label="Formát zadání času"
          >
            <ToggleButton value="SS_CC" sx={{ px: 2, textTransform: 'none', fontFamily: 'monospace' }}>
              SS,cc
            </ToggleButton>
            <ToggleButton value="MM_SS_CC" sx={{ px: 2, textTransform: 'none', fontFamily: 'monospace' }}>
              M:SS,cc
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Displej */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: 2,
          borderColor: isEmpty ? 'divider' : 'primary.main',
          borderRadius: 2,
          px: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
          py: compact ? { xs: 1, sm: 1.5 } : { xs: 2, sm: 2.5 },
          textAlign: 'center',
          mb: compact ? 1 : 1.5,
          boxShadow: isEmpty ? 0 : 1,
          transition: 'all 0.15s',
        }}
        tabIndex={disabled ? -1 : 0}
        autoFocus={autoFocus}
      >
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: compact
              ? { xs: '2.25rem', sm: '2.75rem' }
              : { xs: '3rem', sm: '3.5rem', md: '4rem' },
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.02em',
            color: isEmpty ? 'text.disabled' : 'text.primary',
            userSelect: 'none',
          }}
        >
          {displayText}
        </Typography>
      </Box>

      {showKeyboardHint && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mb: 1 }}
        >
          Zadej čísly z klávesnice (0–9), Backspace = smazat, Esc = vynulovat, Enter = další
        </Typography>
      )}

      {/* Tool row — vzdy viditelny */}
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: showKeypad ? (compact ? 1 : 1.5) : 0 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<BackspaceIcon />}
          onClick={pop}
          disabled={disabled || isEmpty}
        >
          Smazat
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={clear}
          disabled={disabled || isEmpty}
        >
          Vynulovat
        </Button>
        {onNext && (
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={onNext}
            disabled={disabled}
          >
            Další
          </Button>
        )}
      </Stack>

      {/* Numericky keypad — pouze na tabletu/mobilu */}
      {showKeypad && (
        <Grid container spacing={compact ? 0.75 : 1} sx={{ maxWidth: compact ? 360 : 420, mx: 'auto' }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <Grid size={4} key={d}>
              <KeypadButton onClick={() => append(d)} disabled={disabled} compact={compact}>
                {d}
              </KeypadButton>
            </Grid>
          ))}
          <Grid size={4}>
            <KeypadButton onClick={clear} disabled={disabled || isEmpty} color="warning" compact={compact}>
              C
            </KeypadButton>
          </Grid>
          <Grid size={4}>
            <KeypadButton onClick={() => append('0')} disabled={disabled} compact={compact}>
              0
            </KeypadButton>
          </Grid>
          <Grid size={4}>
            <KeypadButton onClick={pop} disabled={disabled || isEmpty} color="error" compact={compact}>
              <BackspaceIcon />
            </KeypadButton>
          </Grid>
        </Grid>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        {displayFormat === 'SS_CC'
          ? 'Zadej celkový čas v setinách. Pro čas nad 1 minutu přepni na „M:SS,cc".'
          : 'Zadávání v minutách: cifry se připisují zprava do pozic M | SS | cc.'}
      </Typography>
    </Box>
  );
};

interface KeypadButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: 'primary' | 'warning' | 'error';
  compact?: boolean;
}

const KeypadButton = ({ children, onClick, disabled, color = 'primary', compact = false }: KeypadButtonProps) => (
  <Button
    fullWidth
    variant="outlined"
    color={color}
    onClick={onClick}
    disabled={disabled}
    sx={{
      minHeight: compact ? { xs: 44, sm: 48 } : { xs: 56, sm: 64 },
      fontSize: compact ? { xs: '1.15rem', sm: '1.35rem' } : { xs: '1.5rem', sm: '1.75rem' },
      fontFamily: 'monospace',
      fontWeight: 600,
    }}
  >
    {children}
  </Button>
);
